import { useEffect } from "react";

const BG = "#F8F4EE";
const BG_CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#6B5D52";
const BORDER = "#E4D9CC";
const GREEN = "#2F6F4E";
const GREEN_BG = "rgba(47, 111, 78, 0.12)";
const ORANGE = "#C26A1A";
const ORANGE_BG = "rgba(194, 106, 26, 0.12)";
const BEIGE_ACTIVE = "linear-gradient(145deg, rgba(192,154,91,0.14) 0%, rgba(248,244,238,0.98) 100%)";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

export default function AnnexesClausesPage({ projectData, setProjectData }) {
  const {
    includeAnnexeA,
    includeAnnexeB,
    includeAnnexeC,
    includeClauseArbitrage,
    includeClauseBlocage,
    includeClauseSubstitution,
  } = projectData;

  function updateField(field, value) {
    setProjectData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function updatePerimetreField(field, value) {
    setProjectData((prev) => ({
      ...prev,
      perimetreMission: {
        ...prev.perimetreMission,
        [field]: value,
      },
    }));
  }

  useEffect(() => {
    const id = "yoma-fonts-annexes-clauses";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700&display=swap";
    document.head.appendChild(link);
  }, []);

  const cabinetName =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("yoma_company_name")) ||
    "MON CABINET";

  const annexItems = [
    {
      key: "includeAnnexeA",
      checked: includeAnnexeA,
      title: "Annexe A — Échéancier de paiement",
      desc: "Calendrier des versements et jalons financiers du mandat.",
    },
    {
      key: "includeAnnexeB",
      checked: includeAnnexeB,
      title: "Annexe B — Périmètre de mission",
      desc: "Détail des phases et prestations couvertes par YOMA.",
    },
    {
      key: "includeAnnexeC",
      checked: includeAnnexeC,
      title: "Annexe C — Planning prévisionnel",
      desc: "Vue d’ensemble du calendrier prévisionnel du projet.",
    },
  ];

  const clauseItems = [
    {
      key: "includeClauseArbitrage",
      checked: includeClauseArbitrage,
      title: "Clause d’arbitrage",
      desc: "Règlement des litiges par voie arbitrale conformément au cadre convenu.",
    },
    {
      key: "includeClauseBlocage",
      checked: includeClauseBlocage,
      title: "Clause de blocage de paiement",
      desc: "Suspension des versements en cas de non-conformité ou de réserve.",
    },
    {
      key: "includeClauseSubstitution",
      checked: includeClauseSubstitution,
      title: "Clause de substitution constructeur",
      desc: "Modalités de remplacement de l’entreprise d’exécution si nécessaire.",
    },
  ];

  const selectedCount =
    [includeAnnexeA, includeAnnexeB, includeAnnexeC, includeClauseArbitrage, includeClauseBlocage, includeClauseSubstitution].filter(
      Boolean
    ).length;
  const dossierComplet = selectedCount === 6;

  const labelStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "10px 0",
    fontFamily: FF_DM,
    fontSize: 14,
    color: TAUPE,
    lineHeight: 1.45,
    cursor: "pointer",
  };

  const phaseTitle = {
    fontFamily: FF_DM,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: GOLD,
    marginTop: 18,
    marginBottom: 8,
  };

  function SelectableCard({ active, onCardClick, title, desc, checked, onCheckChange }) {
    return (
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onCardClick();
          }
        }}
        onClick={onCardClick}
        style={{
          borderRadius: 14,
          border: active ? `2px solid ${GOLD}` : `1px solid ${BORDER}`,
          background: active ? BEIGE_ACTIVE : BG_CARD,
          padding: "16px 18px",
          cursor: "pointer",
          transition: "border-color 0.2s, box-shadow 0.2s, background 0.2s",
          boxShadow: active
            ? "0 8px 28px rgba(192, 154, 91, 0.12)"
            : "0 4px 16px rgba(61, 46, 32, 0.04)",
        }}
      >
        <label style={{ display: "flex", gap: 14, alignItems: "flex-start", cursor: "pointer", margin: 0 }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={onCheckChange}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 18, height: 18, marginTop: 3, accentColor: GOLD, flexShrink: 0 }}
          />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: "block",
                fontFamily: FF_PLAYFAIR,
                fontSize: 17,
                fontWeight: 600,
                color: TAUPE,
                marginBottom: 6,
                lineHeight: 1.25,
              }}
            >
              {title}
            </span>
            <span
              style={{
                display: "block",
                fontFamily: FF_DM,
                fontSize: 13,
                color: MUTED,
                lineHeight: 1.5,
              }}
            >
              {desc}
            </span>
          </span>
        </label>
      </div>
    );
  }

  const contextMessage = dossierComplet
    ? "Ensemble contractuel complet — le dossier est prêt pour intégration au contrat client YOMA."
    : selectedCount === 0
      ? "Aucun élément sélectionné pour l’instant. Activez les annexes et clauses utiles à votre mission."
      : "Sélection partielle : vous pouvez compléter le dossier (annexes + clauses) avant contractualisation.";

  return (
    <>
      <style>{`
        .annexes-clauses-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.12fr) minmax(300px, 0.88fr);
          gap: 28px;
          align-items: start;
        }
        @media (max-width: 960px) {
          .annexes-clauses-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <main
        style={{
          padding: "8px 0 40px",
          margin: 0,
          minHeight: "100%",
          background: BG,
          fontFamily: FF_DM,
          color: TAUPE,
        }}
      >
        <header style={{ marginBottom: 32 }}>
          <div
            style={{
              fontFamily: FF_DM,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 10,
            }}
          >
            {cabinetName}
          </div>
          <h1
            style={{
              fontFamily: FF_PLAYFAIR,
              fontSize: 32,
              fontWeight: 600,
              color: TAUPE,
              margin: "0 0 10px",
              lineHeight: 1.15,
            }}
          >
            Annexes &amp; Clauses
          </h1>
          <p
            style={{
              fontFamily: FF_DM,
              fontSize: 15,
              color: MUTED,
              margin: 0,
              maxWidth: 560,
              lineHeight: 1.55,
            }}
          >
            Composition du dossier contractuel YOMA
          </p>
        </header>

        <div className="annexes-clauses-grid">
          {/* COLONNE GAUCHE */}
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            <section
              style={{
                background: BG_CARD,
                borderRadius: 16,
                border: `1px solid ${BORDER}`,
                boxShadow: "0 12px 40px rgba(61, 46, 32, 0.06)",
                padding: "26px 22px 28px",
              }}
            >
              <h2
                style={{
                  fontFamily: FF_PLAYFAIR,
                  fontSize: 22,
                  fontWeight: 600,
                  color: TAUPE,
                  margin: "0 0 18px",
                }}
              >
                Annexes
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {annexItems.map((item) => (
                  <SelectableCard
                    key={item.key}
                    active={item.checked}
                    checked={item.checked}
                    title={item.title}
                    desc={item.desc}
                    onCardClick={() => updateField(item.key, !item.checked)}
                    onCheckChange={(e) => updateField(item.key, e.target.checked)}
                  />
                ))}
              </div>

              {includeAnnexeB && (
                <div
                  style={{
                    marginTop: 22,
                    padding: "20px 18px",
                    borderRadius: 12,
                    border: `1px solid ${BORDER}`,
                    background: BG,
                  }}
                >
                  <h3
                    style={{
                      fontFamily: FF_PLAYFAIR,
                      fontSize: 18,
                      fontWeight: 600,
                      color: TAUPE,
                      margin: "0 0 6px",
                    }}
                  >
                    Annexe B — Périmètre de mission
                  </h3>

                  <div style={phaseTitle}>Phase 1 — Préparation du projet</div>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.analyseFaisabilite}
                      onChange={(e) => updatePerimetreField("analyseFaisabilite", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Analyse de faisabilité du projet
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.verificationFonciere}
                      onChange={(e) => updatePerimetreField("verificationFonciere", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Vérification foncière et administrative
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.coordinationArchitecte}
                      onChange={(e) => updatePerimetreField("coordinationArchitecte", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Coordination avec l’architecte
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.selectionEntreprise}
                      onChange={(e) => updatePerimetreField("selectionEntreprise", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Sélection de l’entreprise d’exécution
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.contratExecutionTravaux}
                      onChange={(e) => updatePerimetreField("contratExecutionTravaux", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Préparation du contrat d’exécution des travaux
                  </label>

                  <div style={phaseTitle}>Phase 2 — Coordination des travaux</div>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.visitesChantier}
                      onChange={(e) => updatePerimetreField("visitesChantier", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Organisation des visites de chantier
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.comptesRendusVisite}
                      onChange={(e) => updatePerimetreField("comptesRendusVisite", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Rédaction des comptes rendus de visite
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.suiviPlanning}
                      onChange={(e) => updatePerimetreField("suiviPlanning", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Suivi du planning des travaux
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.validationJalons}
                      onChange={(e) => updatePerimetreField("validationJalons", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Validation des jalons techniques
                  </label>

                  <div style={phaseTitle}>Phase 3 — Gestion financière sous mandat</div>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.receptionFonds}
                      onChange={(e) => updatePerimetreField("receptionFonds", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Réception des fonds mandatés
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.comptabiliteAnalytique}
                      onChange={(e) => updatePerimetreField("comptabiliteAnalytique", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Comptabilité analytique du projet
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.versementsEntreprise}
                      onChange={(e) => updatePerimetreField("versementsEntreprise", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Versement des paiements à l’entreprise
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.blocagePaiements}
                      onChange={(e) => updatePerimetreField("blocagePaiements", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Blocage des paiements en cas de non conformité
                  </label>

                  <div style={phaseTitle}>Phase 4 — Clôture du projet</div>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.receptionContradictoire}
                      onChange={(e) => updatePerimetreField("receptionContradictoire", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Organisation de la réception contradictoire
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.procesVerbalReception}
                      onChange={(e) => updatePerimetreField("procesVerbalReception", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Rédaction du procès verbal de réception
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.leveeReserves}
                      onChange={(e) => updatePerimetreField("leveeReserves", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Suivi de la levée des réserves
                  </label>
                  <label style={labelStyle}>
                    <input
                      type="checkbox"
                      checked={projectData.perimetreMission.remiseDossierCloture}
                      onChange={(e) => updatePerimetreField("remiseDossierCloture", e.target.checked)}
                      style={{ accentColor: GOLD, width: 17, height: 17, marginTop: 2, flexShrink: 0 }}
                    />
                    Remise du dossier de clôture du projet
                  </label>
                </div>
              )}
            </section>

            <section
              style={{
                background: BG_CARD,
                borderRadius: 16,
                border: `1px solid ${BORDER}`,
                boxShadow: "0 12px 40px rgba(61, 46, 32, 0.06)",
                padding: "26px 22px 28px",
              }}
            >
              <h2
                style={{
                  fontFamily: FF_PLAYFAIR,
                  fontSize: 22,
                  fontWeight: 600,
                  color: TAUPE,
                  margin: "0 0 18px",
                }}
              >
                Clauses
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {clauseItems.map((item) => (
                  <SelectableCard
                    key={item.key}
                    active={item.checked}
                    checked={item.checked}
                    title={item.title}
                    desc={item.desc}
                    onCardClick={() => updateField(item.key, !item.checked)}
                    onCheckChange={(e) => updateField(item.key, e.target.checked)}
                  />
                ))}
              </div>
            </section>
          </div>

          {/* COLONNE DROITE — Dossier retenu */}
          <div
            style={{
              background: BG_CARD,
              borderRadius: 16,
              border: `2px solid ${GOLD}`,
              boxShadow: "0 16px 48px rgba(192, 154, 91, 0.12)",
              padding: "28px 24px 26px",
              position: "sticky",
              top: 16,
            }}
          >
            <h2
              style={{
                fontFamily: FF_PLAYFAIR,
                fontSize: 24,
                fontWeight: 600,
                color: TAUPE,
                margin: "0 0 16px",
              }}
            >
              Dossier retenu
            </h2>

            <div style={{ marginBottom: 18 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: FF_DM,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  padding: "8px 14px",
                  borderRadius: 999,
                  background: dossierComplet ? GREEN_BG : ORANGE_BG,
                  color: dossierComplet ? GREEN : ORANGE,
                  border: `1px solid ${dossierComplet ? "rgba(47,111,78,0.35)" : "rgba(194,106,26,0.35)"}`,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: dossierComplet ? GREEN : ORANGE,
                  }}
                />
                {dossierComplet ? "Dossier complet" : "Dossier incomplet"}
              </span>
            </div>

            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: "0 0 20px",
                fontFamily: FF_DM,
                fontSize: 14,
                lineHeight: 1.65,
                color: TAUPE,
              }}
            >
              {includeAnnexeA && (
                <li style={{ padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                  Annexe A — Échéancier de paiement
                </li>
              )}
              {includeAnnexeB && (
                <li style={{ padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                  Annexe B — Périmètre de mission
                </li>
              )}
              {includeAnnexeC && (
                <li style={{ padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                  Annexe C — Planning prévisionnel
                </li>
              )}
              {includeClauseArbitrage && (
                <li style={{ padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                  Clause d’arbitrage
                </li>
              )}
              {includeClauseBlocage && (
                <li style={{ padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                  Clause de blocage de paiement
                </li>
              )}
              {includeClauseSubstitution && (
                <li style={{ padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                  Clause de substitution constructeur
                </li>
              )}
              {selectedCount === 0 && (
                <li style={{ padding: "12px 0", color: MUTED, fontStyle: "italic" }}>
                  Aucun élément pour l’instant.
                </li>
              )}
            </ul>

            <div
              style={{
                paddingTop: 16,
                borderTop: `1px solid ${BORDER}`,
                fontFamily: FF_DM,
                fontSize: 13,
                lineHeight: 1.7,
                color: MUTED,
              }}
            >
              {contextMessage}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
